// Shared helpers: Australian dates, status colours, small utilities.

export function toAus(iso?: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function fromAus(aus: string): string | null {
  const s = (aus || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

export function todayDarwin(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Darwin' }).format(new Date());
}

export function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + months, d));
  if (dt.getUTCDate() !== d) dt.setUTCDate(0);
  return dt.toISOString().slice(0, 10);
}

export function diffDays(a: string, b: string): number {
  return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);
}

export function eachDate(from: string, to: string): string[] {
  const out: string[] = [];
  let d = from;
  while (d <= to) { out.push(d); d = addDays(d, 1); }
  return out;
}

export const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function dowOf(iso: string): number {
  return new Date(iso + 'T12:00:00Z').getUTCDay();
}

export function fmtDayLabel(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${DOW[dowOf(iso)]} ${d}/${m}`;
}

// TPA's standard roster patterns. Must match ROSTER_PATTERNS in server/services/roster.js.
export const ROSTER_PATTERNS = [
  '10 days on / 4 days off',
  '3 weeks on / 1 week off',
  '6 days on / 1 day off',
  '1 week on / 2 weeks off',
  'Custom',
  'One-off visit'
];

// Position level. Separate from Trade (the ticket) and Employment type (who pays them).
export const ROLE_OPTIONS = [
  'Project Manager',
  'Site Manager',
  'Site Supervisor',
  'Foreman',
  'Leading Hand',
  'Tradesperson',
  'Apprentice',
  'Labourer',
  'Engineer',
  'Safety Advisor',
  'Surveyor',
  'Administrator',
  'Consultant',
  'Client Representative',
  'Visitor'
];

export const EMPLOYMENT_TYPES = ['Employee', 'Labour hire', 'Subcontractor', 'Consultant', 'Visitor'];

export const ATTENDANCE_STATUSES = [
  'planned', 'tentative', 'confirmed', 'accommodation_requested', 'accommodation_booked',
  'checked_in', 'checked_out', 'cancelled'
];

export const BOOKING_STATUSES = [
  'forecast', 'draft', 'request_to_send', 'requested', 'awaiting_confirmation',
  'confirmed', 'partially_confirmed', 'amended', 'cancelled', 'completed'
];

export const STATUS_COLOURS: Record<string, string> = {
  planned: '#8a93a6',
  tentative: '#c9a227',
  confirmed: '#78815F',
  accommodation_requested: '#5f7a81',
  accommodation_booked: '#3e6b4f',
  checked_in: '#2e7d32',
  checked_out: '#5c5c5c',
  cancelled: '#b23a3a',
  on_leave: '#7e57c2',
  travel: '#4f83cc',
  forecast: '#8a93a6',
  draft: '#8a93a6',
  request_to_send: '#c9a227',
  requested: '#c98227',
  awaiting_confirmation: '#c9a227',
  partially_confirmed: '#5f8161',
  amended: '#4f83cc',
  completed: '#5c5c5c'
};

export function statusLabel(s: string): string {
  return (s || '').replace(/_/g, ' ');
}

// Distinct colours for work areas / trades when colouring the timeline by assignment
// rather than by status. Chosen to stay legible with white text in both themes.
export const AREA_PALETTE = [
  '#78815F', '#4f83cc', '#c98227', '#7e57c2', '#2e7d32', '#b23a3a',
  '#5f7a81', '#3e6b4f', '#a0522d', '#6a5acd', '#20808d', '#9c6644',
  '#556b2f', '#8b3a62', '#8a6d3b', '#46698c'
];

export function paletteColour(key: string | number | null | undefined, keys: (string | number)[]): string {
  if (key === null || key === undefined) return '#8a93a6';
  const i = keys.indexOf(key);
  return AREA_PALETTE[(i < 0 ? 0 : i) % AREA_PALETTE.length];
}

export function money(n?: number | null): string {
  if (n === null || n === undefined) return '';
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

export function csvDownload(filename: string, columns: string[], rows: any[][]) {
  const esc = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const text = '﻿' + [columns, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function fullName(p: { first_name?: string; last_name?: string; preferred_name?: string | null }): string {
  return `${p.preferred_name || p.first_name || ''} ${p.last_name || ''}`.trim();
}
